import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { getErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!token) { toast.error(t.reset.no_token); return; }
    if (password.length < 6) { toast.error(t.reset.too_short); return; }
    if (password !== confirm) { toast.error(t.reset.mismatch); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t.reset.success);
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(getErrorDetail(err));
    } finally { setLoading(false); }
  }

  return (
    <main data-testid="reset-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 md:p-16">
      <form onSubmit={submit} className="w-full max-w-md space-y-6 bg-white p-8 rounded-2xl border border-[hsl(var(--border))]" data-testid="reset-form">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
          <ArrowLeft className="w-3 h-3" />{t.forgot.back_to_login}
        </Link>
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">{t.reset.title}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t.reset.sub}</p>
          {!token && <p className="text-sm text-[hsl(var(--destructive))] mt-2" data-testid="reset-no-token">{t.reset.no_token}</p>}
        </div>
        <div>
          <Label htmlFor="rp-new" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.reset.new_password}</Label>
          <Input id="rp-new" type="password" minLength={6} required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="reset-password" className="mt-2" />
        </div>
        <div>
          <Label htmlFor="rp-conf" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{t.reset.confirm_password}</Label>
          <Input id="rp-conf" type="password" minLength={6} required value={confirm} onChange={(e)=>setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-2" />
        </div>
        <Button type="submit" disabled={loading || !token} data-testid="reset-submit" className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">
          {loading ? "…" : t.reset.submit}
        </Button>
      </form>
    </main>
  );
}
